import { useState } from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { initials } from "@/components/app/EmpAvatar";
import {
  LayoutDashboard, Activity, BarChart3, Building2, Users, Box, Sparkles,
  CreditCard, FileText, Package, ClipboardCheck, Tag, Sliders, Palette,
  Globe, Mail, Database, ShieldCheck, AlertCircle, LifeBuoy, MessagesSquare,
  Settings, Menu, X, ChevronDown, LogOut, KeyRound, HardDrive, Lock, Zap,
  LayoutGrid, MessageCircle, UserCog,
} from "lucide-react";

/**
 * QrewHR Platform Console — premium light SaaS shell, strictly separate from HRM.
 * Only platform_owner can render this layout (guarded). No HRM modules here.
 */

const NAV = [
  /* OVERVIEW */
  { to: "/master",                 label: "Platform Dashboard",  icon: LayoutDashboard, group: "overview", end: true },
  { to: "/master/client-health",   label: "Client Health",       icon: Activity,        group: "overview" },
  { to: "/master/usage",           label: "Usage Analytics",     icon: BarChart3,       group: "overview" },

  /* CLIENTS */
  { to: "/master/tenants",         label: "Tenants / Clients",   icon: Building2,       group: "clients" },
  { to: "/master/tenant-users",    label: "Tenant Users",        icon: Users,           group: "clients" },
  { to: "/master/demo",            label: "Demo Workspaces",     icon: Box,             group: "clients" },
  { to: "/master/impersonation",   label: "Impersonation Logs",  icon: Sparkles,        group: "clients" },

  /* SALES & BILLING */
  { to: "/plans-admin",            label: "Plans Management",    icon: Tag,             group: "billing" },
  { to: "/master/pricing",         label: "Pricing",             icon: CreditCard,      group: "billing" },
  { to: "/master/subscriptions",   label: "Subscriptions",       icon: ClipboardCheck,  group: "billing" },
  { to: "/platform-billing",       label: "Billing Management",  icon: FileText,        group: "billing" },
  { to: "/master/invoices",        label: "Invoices",            icon: FileText,        group: "billing" },
  { to: "/master/license-keys",    label: "License Keys",        icon: ShieldCheck,     group: "billing" },
  { to: "/master/trials",          label: "Trial Management",    icon: Package,         group: "billing" },

  /* PRODUCT CONTROL */
  { to: "/master/feature-controls", label: "Feature Controls",   icon: Sliders,         group: "product" },
  { to: "/master/module-access",    label: "Module Access",      icon: KeyRound,        group: "product" },
  { to: "/master/brand-content",    label: "Brand & Content",    icon: Palette,         group: "product" },
  { to: "/master/widget-manager",   label: "Widget Manager",     icon: LayoutGrid,      group: "product" },
  { to: "/master/email-pdf",        label: "Email & PDF",        icon: Mail,            group: "product" },

  /* OPERATIONS */
  { to: "/master/backups",         label: "Backup & Restore",    icon: Database,        group: "ops" },
  { to: "/master/system-health",   label: "System Health",       icon: Activity,        group: "ops" },
  { to: "/master/api-usage",       label: "API Usage",           icon: BarChart3,       group: "ops" },
  { to: "/master/storage-usage",   label: "Storage Usage",       icon: HardDrive,       group: "ops" },
  { to: "/master/error-logs",      label: "Error Logs",          icon: AlertCircle,     group: "ops" },
  { to: "/master/audit-logs",      label: "Audit Logs",          icon: ClipboardCheck,  group: "ops" },
  { to: "/master/security-logs",   label: "Security Logs",       icon: Lock,            group: "ops" },

  /* SUPPORT */
  { to: "/master/tickets",         label: "Support Tickets",     icon: LifeBuoy,        group: "support" },
  { to: "/master/client-requests", label: "Client Requests",     icon: MessageCircle,   group: "support" },
  { to: "/master/announcements",   label: "Client Announcements",icon: MessagesSquare,  group: "support" },

  /* SETTINGS */
  { to: "/master/settings",        label: "Platform Settings",   icon: Settings,        group: "settings" },
  { to: "/master/admin-users",     label: "Admin Users",         icon: Users,           group: "settings" },
  { to: "/master/roles",           label: "Roles & Permissions", icon: UserCog,         group: "settings" },
  { to: "/master/integrations",    label: "Integrations",        icon: Zap,             group: "settings" },
];

const GROUPS = {
  overview: { label: "Overview",        dot: "bg-violet-500"  },
  clients:  { label: "Clients",         dot: "bg-blue-500"    },
  billing:  { label: "Sales & Billing", dot: "bg-emerald-500" },
  product:  { label: "Product Control", dot: "bg-fuchsia-500" },
  ops:      { label: "Operations",      dot: "bg-orange-500"  },
  support:  { label: "Support",         dot: "bg-rose-500"    },
  settings: { label: "Settings",        dot: "bg-slate-500"   },
};

export default function PlatformLayout() {
  const { user, loading, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();

  if (loading) return null;
  // Strict guard: not signed in → admin login. Not platform owner → HRM workspace.
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_platform_owner) return <Navigate to="/" replace />;

  const grouped = Object.entries(GROUPS).map(([k, v]) => ({
    key: k, label: v.label, dot: v.dot, items: NAV.filter((n) => n.group === k),
  }));

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="platform-console-layout">
      {mobileOpen && <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* ============= SIDEBAR — premium light theme ============= */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 z-40 w-[270px] h-screen bg-white border-r border-slate-200 flex flex-col shrink-0 transition-transform duration-200`}
        data-testid="platform-sidebar"
      >
        {/* Brand header — gradient strip */}
        <Link to="/master" className="relative flex items-center gap-3 px-5 py-4 border-b border-slate-200 overflow-hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-orange-400" />
          <div className="w-11 h-11 rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 shrink-0 flex items-center justify-center shadow-sm">
            <img src="/qrewhr-logo.jpg" alt="Qrew HR" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold font-[Outfit] text-[16px] leading-tight tracking-tight text-slate-900 truncate">Qrew HR</div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold mt-0.5">
              <span className="qh-grad-text">Platform Console</span>
            </div>
          </div>
        </Link>

        {/* Owner badge */}
        <div className="px-3 pt-3">
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-violet-50 via-fuchsia-50 to-orange-50 border border-violet-100" data-testid="platform-owner-badge">
            <ShieldCheck className="w-4 h-4 text-violet-700 shrink-0" />
            <div className="text-[11px] font-semibold text-slate-700 leading-tight">
              <span className="block text-violet-800">Platform Owner</span>
              <span className="text-[10px] text-slate-500 truncate">{user.email}</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 no-scrollbar mt-1">
          {grouped.map(({ key, label, dot, items }) => (
            <div key={key} className="mb-3">
              <div className="flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                {label}
              </div>
              <div className="mt-1 space-y-0.5">
                {items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`platform-nav-${n.to.replace(/^\/master\/?/, "").replace(/^\//, "") || "dashboard"}`}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 h-[40px] text-[13px] rounded-lg transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-violet-100 to-fuchsia-50 text-violet-800 font-semibold ring-1 ring-violet-200 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-full before:bg-violet-500"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`
                    }
                  >
                    <n.icon className="w-[17px] h-[17px] shrink-0" />
                    <span className="flex-1 truncate">{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-4 py-3 border-t border-slate-200 flex items-center gap-2 text-[10px] text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>v1.9 · Platform Console</span>
        </div>
      </aside>

      {/* ============= MAIN ============= */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-4 sticky top-0 z-30" data-testid="platform-topbar">
          <button onClick={() => setMobileOpen((v) => !v)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="platform-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold">
              <span className="qh-grad-text">Platform Console</span>
            </div>
            <div className="text-sm font-semibold text-slate-900 truncate">Manage clients, plans, branding & operations</div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 h-10 pl-1 pr-2 sm:pr-3 rounded-lg hover:bg-slate-100 transition" data-testid="platform-user-menu">
                <Avatar className="w-9 h-9 ring-2 ring-violet-200">
                  <AvatarImage src={user.avatar_url || user.profile_picture} alt={user.full_name} />
                  <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-xs font-semibold">{initials(user.full_name)}</AvatarFallback>
                </Avatar>
                <div className="text-left leading-tight hidden md:block max-w-[160px]">
                  <div className="text-sm font-semibold text-slate-900 truncate">{user.full_name}</div>
                  <div className="text-[11px] text-violet-700 font-semibold truncate">Platform Owner</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Platform Owner</DropdownMenuLabel>
              <div className="px-2 pb-2 text-xs text-slate-500 truncate">{user.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/master/admin-users")} data-testid="platform-menu-admins">
                <Users className="w-4 h-4 mr-2" /> Admin Users
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/master/settings")} data-testid="platform-menu-settings">
                <Settings className="w-4 h-4 mr-2" /> Platform Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="platform-menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Content */}
        <main className="flex-1 min-w-0 p-5 lg:p-7 bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
